/**
 * MC Fleet Leaderboard — metric selector, window buttons, AJAX re-render (LEAD-02, D-04, D-08, D-15).
 *
 * Initializes on /mc/leaderboard only (guards on #mc-metric-select presence, the one element the
 * template renders unconditionally — the board itself, the empty card and the per-server race are
 * all mutually-exclusive branches, so none of them is a safe presence marker).
 *
 * D-04: changing the metric or the window RE-FETCHES a freshly server-ranked list over the Nette
 * signal. Nothing here re-sorts locally. The server owns ranking (competition ranks, tie handling,
 * ratio recombination, fixture exclusion); a client-side sort would be a second implementation of
 * all of it, free to disagree with the one the page was rendered from.
 *
 * ⚠ EVERY CELL IS BUILT WITH createElement + textContent. The markup-assignment property is used
 * NOWHERE in this file, deliberately and by grep-enforced acceptance criterion. Display names and
 * metric labels originate OUTSIDE this codebase (Mojang profiles, mods, datapacks). The
 * server-rendered path is protected by Latte's auto-escaping; this path has no such backstop and is
 * protected by construction instead (T-228-09-01).
 *
 * ⚠ SIGNAL URLS ARE `?do=<signalName>`, never a Latte link macro — a template macro has no meaning
 * in a TS file. The signal name is the handler name minus `handle`, first letter lowercased:
 * handleGetLeaderboard -> `?do=getLeaderboard`. The `X-Requested-With` header is REQUIRED; Nette's
 * isAjax() check depends on it.
 *
 * ⚠ Both rules above are enforced by LITERAL grep, so neither is spelled out verbatim here — a rule
 * written out in a comment would fail its own check. Same discipline as leaderboard.latte's header.
 *
 * ABSENT IS NOT ZERO. A null value means UNRANKED, and renders as an em-dash. Never `?? 0`. The
 * same rule binds a windowed row carrying `baseline_incomplete`: the model's 0.0 there means "no
 * earlier row to diff against", not "played nothing", and it renders as an em-dash too.
 *
 * ── Phase 239 plan 09 — D-05/D-06/D-15 server picker ────────────────────────────────────────────
 * Binds #mc-leaderboard-server-select (this page's own DOM marker id, distinct from feed's
 * #mc-server-select and heatmap's #mc-heatmap-server-select so a shared module cannot double-bind).
 *
 * The selection lives in exactly two places: the <select> element and the URL, the latter written
 * through history.replaceState only. NOTHING is persisted — not client-side, not server-side.
 */

// ---------------------------------------------------------------------------
// Payload types — McAnalyticsModel::getLeaderboard() / getAdvancementRace()
// ---------------------------------------------------------------------------

interface LeaderboardMetric {
    key: string;
    label: string;
    unit: string;
    scope: string;
    windowable: boolean;
}

interface LeaderboardRow {
    uuid: string;
    display_name: string;
    unresolved: boolean;
    is_online_mode: boolean;
    value: number | null;
    rank: number;
    unit: string;
    partial: boolean;
    servers: number;
    /**
     * D-05: this player's first reading inside the window IS their baseline, so the accompanying
     * `value` is a 0.0 that means "nothing to diff against" — never "measured zero". Optional in
     * the type because the payload is decoded from the wire, not constructed here.
     */
    baseline_incomplete?: boolean;
}

interface RacePlayer {
    uuid: string;
    display_name: string;
    unresolved: boolean;
    is_online_mode: boolean;
    rank: number;
    completed: number;
    total: number;
    percent: number | null;
}

interface RaceGroup {
    heading: string;
    scope_note: string;
    server_name: string;
    ranked: number;
    players: RacePlayer[];
    /**
     * D-15: the group's OWN server, which its rows deep-link with even under "All servers". The
     * payload keys the group by ID (getAdvancementRace() groups by mc_server_id), while a
     * `?server=` deep-link needs the KEY — so the id is mapped back through the picker's own
     * <option data-server-id> attributes rather than by widening the model's payload.
     */
    mc_server_id: number;
}

/**
 * D-08/D-15 coverage counts, narrowed to the selected server when one is selected. Exactly the
 * shape McAnalyticsModel::getLaneBCoverage() returns, and the shape leaderboard.latte reads.
 */
interface LeaderboardCoverage {
    total: number;
    reporting: number;
    not_reporting: number;
    not_reporting_names: string[];
}

/**
 * CR-05 (239-REVIEW): the resolved scope, shipped WITH the board so this renderer and
 * leaderboard.latte read ONE source instead of two.
 *
 * Without it, `renderBoard()` replaced only #mc-board-region and left `.mc-coverage` — which lives
 * outside that region and is always visible — saying "Ranking N of M servers" above a board showing
 * one server, and printed hardcoded fleet-wide empty copy ("No server has reported X yet") for a
 * server that is simply dark. Both are the fleet-wide-claim-the-viewer-did-not-ask-for that D-08
 * and D-15 exist to prevent, fixed on the first paint and unfixed on the interaction that reaches
 * it.
 *
 * Optional in the type because the payload is decoded from the wire, not constructed here: an older
 * cached bundle talking to a newer backend (or the reverse) degrades to leaving the server-rendered
 * coverage line alone rather than throwing.
 */
interface LeaderboardScope {
    is_scoped: boolean;
    server_names: string[];
    coverage: LeaderboardCoverage;
}

interface LeaderboardPayload {
    has_data: boolean;
    metric: LeaderboardMetric | null;
    window: string;
    rows: LeaderboardRow[];
    ranked: number;
    unranked: number;
    empty_reason: string | null;
    empty_kind: string | null;
    window_label: string;
    /**
     * Present ONLY when the selected metric's scope is per-server (the advancements case). The
     * flat-board payload for such a metric is an explicit named-empty, so without these groups the
     * AJAX path would replace the D-14 per-server race with a bare "ranked within each server" card
     * — strictly worse than the server render it replaced. See 228-09-SUMMARY deviation 1.
     */
    advancement_groups?: RaceGroup[];
    /** CR-05: the resolved scope behind THIS payload. See LeaderboardScope above. */
    scope?: LeaderboardScope;
    /**
     * Phase 273 D-07/AFK-03 (Plan 12): uuid -> Playtime total, present ONLY when the selected
     * metric is Active Playtime. Mirrors McPresenter::actionLeaderboard()'s
     * $template->activePlaytimeTotals (plan 273-11) — resolveActivePlaytimeTotals() is the ONE
     * shared PHP-side source for both. Optional in the type because the payload is decoded from the
     * wire, not constructed here: an older cached bundle talking to a newer backend (or the
     * reverse) degrades to no stacked secondary line rather than throwing.
     */
    activePlaytimeTotals?: Record<string, number>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let currentMetric = '';
let currentWindow = '';

/**
 * D-05/D-06/Phase 249 (D-09/D-10/D-11): the checked server keys, or `[]` — the D-10 "all servers"
 * state.
 *
 * Initialised from the SERVER-RENDERED <select multiple>, never from the raw query string: the
 * presenter has already resolved (and, for an unknown/archived/fixture key, already dropped) the
 * keys that actually took effect, so reading the element's `selectedOptions` is reading the truth
 * rather than a second opinion.
 *
 * The client-side option-existence check below is a UX affordance ONLY — it is emphatically not
 * the security control. Every request re-validates `server[]` server-side through
 * resolveScopedServerIds()'s allow-list (T-239-V5-01/T-249-V5-01).
 */
let currentServers: string[] = [];

/**
 * mc_server_id → server key, read once from the picker's own <option data-server-id> attributes.
 *
 * D-15's advancement-group rule needs a KEY, and the group payload carries only an ID. Building
 * the map from markup already on the page keeps the model's payload untouched and cannot drift
 * from the option list, because it IS the option list.
 */
const serverKeyById = new Map<number, string>();

/**
 * Idempotency guard. A shared component initialising on every page and binding twice is a known
 * failure mode in this repo, and a double-bound selector fires two concurrent fetches whose
 * responses can land out of order — the later render winning with the earlier metric's rows.
 */
let listenersBound = false;

/**
 * Monotonic request token. The board is re-rendered only by the MOST RECENT request, so a slow
 * response for a metric the user has already moved on from cannot overwrite a newer one.
 */
let requestSeq = 0;

/** The `?uuid=` deep-link origin row, read once at init. */
let highlightUuid: string | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initLeaderboard(): void {
    const select = document.getElementById('mc-metric-select') as HTMLSelectElement | null;
    if (!select) return; // Only runs on /mc/leaderboard

    currentMetric = select.value || select.dataset.metric || '';
    currentWindow = readActiveWindow();
    highlightUuid = new URLSearchParams(window.location.search).get('uuid');

    // D-06/Phase 249 (D-09/D-10/D-11): feed.ts's `all`-FALLBACK picker variant, NOT heatmap.ts's
    // no-fallback one — this board genuinely has an "All servers" state (an empty selection), and a
    // deep link naming a server this picker does not offer must land on that state rather than a
    // blank scope.
    //
    // Every `server[]` value is read, plus the legacy singular `server` value so an old bookmark
    // still pre-selects. CSS.escape guards the attribute selector against each untrusted value
    // (T-239-V5-04/T-249-08-T1), copying feed.ts's shipped guard verbatim. A value with no matching
    // <option> is skipped entirely rather than echoed back — which is also exactly what the server
    // already did with it (T-239-V5-03/T-249-V5-03).
    const serverSelect = document.getElementById('mc-leaderboard-server-select') as HTMLSelectElement | null;
    if (serverSelect) {
        const params = new URLSearchParams(window.location.search);
        const deepLinkServers = [...params.getAll('server[]'), ...params.getAll('server')];
        for (const deepLinkServer of deepLinkServers) {
            const option = serverSelect.querySelector(`option[value="${CSS.escape(deepLinkServer)}"]`);
            if (option) (option as HTMLOptionElement).selected = true;
        }

        currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);

        serverKeyById.clear();
        for (const option of Array.from(serverSelect.options)) {
            const id = Number(option.dataset.serverId);
            // There is no `all` sentinel option any more (D-10), so every real option carries a
            // data-server-id and this guard is trivially true — kept anyway rather than removed, a
            // defensive check earns its keep even once its original trigger case is gone.
            if (Number.isInteger(id)) serverKeyById.set(id, option.value);
        }
    }

    // Apply the deep-link highlight to the SERVER-rendered rows immediately; re-applied after every
    // AJAX render too, since those rows are freshly constructed.
    applyHighlight();

    if (listenersBound) return;
    listenersBound = true;

    // Inside the guard, with every other listener in this module: a shared component initialised
    // on every page and binding twice is a documented failure class in this repo, and a
    // double-bound picker fires two concurrent board fetches per change.
    if (serverSelect) {
        serverSelect.addEventListener('change', () => {
            currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);
            void fetchBoard();
        });
    }

    select.addEventListener('change', () => {
        currentMetric = select.value;
        void fetchBoard();
    });

    document.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const key = btn.dataset.window;
            if (!key) return; // a button with no window key drives nothing

            currentWindow = key;

            // The exact active-class toggle idiom analytics.ts uses.
            document.querySelectorAll('.mc-range-btn').forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            void fetchBoard();
        });
    });
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchBoard(): Promise<void> {
    const seq = ++requestSeq;

    // history.replaceState, NOT a redirect and NOT a server-side persisted preference. This repo has
    // a documented bug class where persisted dashboard query params let one visitor (or a probe)
    // clobber another's saved state; a read-only board must not acquire that behaviour. replaceState
    // keeps the selection shareable and refresh-durable without writing anything.
    syncUrl();

    // D-06/Phase 249: the signal narrows through the SAME readServerKeys()/resolveScopedServerIds()
    // call the action does, so the AJAX board and the first paint cannot disagree about scope. One
    // `server[]=` term per checked key, and NO server term at all when the array is empty (D-10's
    // "all servers" state).
    const serverTerms = currentServers
        .map((v) => `&server[]=${encodeURIComponent(v)}`)
        .join('');
    const url = `?do=getLeaderboard&metric=${encodeURIComponent(currentMetric)}`
        + `&window=${encodeURIComponent(currentWindow)}`
        + serverTerms;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-leaderboard] Board fetch failed:', resp.status);
            if (seq === requestSeq) renderFetchError();
            return;
        }

        const payload: LeaderboardPayload = await resp.json();
        if (seq !== requestSeq) return; // a newer request has already been issued

        renderBoard(payload);
    } catch (err) {
        console.error('[mc-leaderboard] Board fetch error:', err);
        if (seq === requestSeq) renderFetchError();
    }
}

function syncUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.set('metric', currentMetric);
    url.searchParams.set('window', currentWindow);
    // The selection rides in the URL and NOWHERE else — neither browser storage API is used
    // anywhere in this module, and nothing is written server-side. This repo has a documented bug
    // class where persisted dashboard query params let one visitor (or a probe) clobber another's
    // state; a read-only board must not acquire it (T-239-STATE-01). replaceState — never the
    // history-appending sibling — also means no history entry per change, so Back still leaves the
    // page rather than walking every selection the viewer tried.
    //
    // ⚠ All three of those absences are enforced by LITERAL grep, so none of the three API names
    // is spelled out above: a rule written verbatim in a comment fails its own check. Same
    // discipline as this file's header and leaderboard.latte's.
    //
    // Phase 249 (T-249-08-T2): BOTH the legacy scalar `server` key and the bracket `server[]` key
    // are deleted before appending, so a stale bookmarked scalar can never survive alongside the
    // new list and be read as an extra key server-side.
    url.searchParams.delete('server');
    url.searchParams.delete('server[]');
    for (const v of currentServers) {
        url.searchParams.append('server[]', v);
    }
    window.history.replaceState(null, '', url.toString());
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderBoard(payload: LeaderboardPayload): void {
    const region = boardRegion();
    region.replaceChildren();

    const metricLabel = payload.metric === null ? 'this ranking' : payload.metric.label;

    // CR-05: the always-visible coverage sentence lives OUTSIDE #mc-board-region, so replacing the
    // region never touched it and it kept making a fleet-wide claim over a scoped board. Rebuilt
    // first, from the payload's own resolved scope, so it can never describe a different scope than
    // the rows rendered below it.
    renderCoverage(payload.scope);

    // The scoped/unscoped copy below reads the SAME resolved scope the sentence above does, so the
    // two can never disagree either. Absent scope (an older bundle, a cached payload) degrades to
    // the fleet-wide wording, which is exactly what the pre-picker page said.
    //
    // Phase 249: mirrors leaderboard.latte's $selectedServerNames/$isSingleServerScoped pair — one
    // or more names means scoped, and isSingleServerScoped narrows to exactly one for the sentences
    // below whose grammar depends on the count.
    const selectedServerNames = payload.scope?.is_scoped === true ? payload.scope.server_names : [];
    const isSingleServerScoped = selectedServerNames.length === 1;
    const selectedServerNamesJoined = selectedServerNames.join(', ');

    // D-14: a per-server metric renders as stacked per-server races, NEVER as one merged
    // fleet-wide list. The flat board payload for such a metric is deliberately an empty one.
    if (payload.metric !== null && payload.metric.scope === 'per-server') {
        const groups = payload.advancement_groups ?? [];
        if (groups.length === 0) {
            // D-08, mirroring leaderboard.latte's own branch: "No server has reported X" over a
            // board scoped to one server is a fleet-wide claim the viewer did not ask for.
            region.appendChild(emptyCard(
                'Nothing to rank yet',
                selectedServerNames.length > 0
                    ? `${selectedServerNamesJoined} ${isSingleServerScoped ? "hasn't" : "haven't"} `
                        + `reported ${metricLabel.toLowerCase()} yet.`
                    : `No server has reported ${metricLabel.toLowerCase()} yet.`,
            ));
            return;
        }
        for (const group of groups) {
            region.appendChild(raceSection(group));
        }
        applyHighlight();
        return;
    }

    // D-15, Lane B darkness — a DISTINCT card from "this metric is empty", and the branch this
    // renderer was missing entirely: without it a dark server fell through to the generic empty
    // card below and printed the model's fleet-wide empty_reason verbatim. Ordered exactly as
    // leaderboard.latte orders it (after the per-server branch, before the per-metric one), and
    // read off the same narrowed coverage the template reads.
    if (payload.scope !== undefined && payload.scope.coverage.reporting === 0) {
        region.appendChild(selectedServerNames.length > 0
            ? emptyCard(
                isSingleServerScoped ? "This server isn't reporting Lane B yet" : "These servers aren't reporting Lane B yet",
                `${selectedServerNamesJoined} ${isSingleServerScoped ? 'has' : 'have'} never sent a `
                + `daily snapshot, so nobody on ${isSingleServerScoped ? 'it' : 'them'} can be ranked. `
                + `That is not the same as nobody playing — once ${isSingleServerScoped ? 'its' : 'their'} `
                + 'mod sends its first snapshot, players will appear here automatically.',
            )
            : emptyCard(
                "Fleet leaderboard isn't live yet",
                'No server is reporting Lane B data yet. Once a server\'s mod sends its first '
                + 'daily snapshot, players will appear here automatically.',
            ));
        return;
    }

    // D-15 on the AJAX path. A blank table that merely did not throw is this repo's documented
    // false-GREEN; the reason the board is empty is NAMED, exactly as the first paint names it.
    if (!payload.has_data) {
        const title = payload.empty_kind === 'not_windowable'
            ? 'Not available for this window'
            : 'Nothing to rank yet';
        // D-08: only the `no_data` kind is a statement ABOUT A POPULATION, so only it re-points at
        // the selected server. `not_windowable` (and every other kind) is a statement about the
        // METRIC or the WINDOW, and re-wording those per-server would attach a false cause — the
        // same class of lie in the opposite direction. Identical rule to leaderboard.latte's.
        const body = selectedServerNames.length > 0 && payload.empty_kind === 'no_data'
            ? `Nobody on ${selectedServerNamesJoined} has any ${metricLabel.toLowerCase()} yet.`
            : (payload.empty_reason ?? '');
        const card = emptyCard(title, body);
        if (payload.unranked > 0) {
            const note = document.createElement('p');
            note.className = 'text-muted small mb-0 mt-2';
            note.textContent = `${payload.unranked} ${payload.unranked === 1 ? 'player has' : 'players have'} `
                + `no measurement for ${metricLabel.toLowerCase()} — unranked, not zero.`;
            card.querySelector('.ksat-empty-state')?.appendChild(note);
        }
        region.appendChild(card);
        return;
    }

    region.appendChild(boardMeta(payload));
    region.appendChild(boardTable(payload, metricLabel));
    applyHighlight();
}

/**
 * CR-05 (239-REVIEW) — rebuilds `.mc-coverage` from the payload's resolved scope.
 *
 * A NODE-FOR-NODE mirror of leaderboard.latte's coverage block (both branches, the <details>
 * disclosure shape, and D-15's load-bearing "that is not the same as nobody played" note), because
 * the sentence is ALWAYS VISIBLE and sits outside the region `renderBoard()` replaces — so before
 * this existed it survived every picker change and kept counting the fleet above a one-server board.
 *
 * The element is rebuilt IN PLACE rather than moved into #mc-board-region: the region is anchored
 * immediately after `.mc-analytics-selector`, and relocating the coverage line would put it below
 * the selectors instead of above them.
 *
 * Built with createElement + textContent like every other renderer in this file — server names come
 * from outside this codebase and this path has no auto-escaping backstop.
 *
 * An absent `scope` (older bundle / newer backend, or the reverse) leaves the server-rendered
 * sentence exactly as Latte emitted it rather than blanking it.
 */
function renderCoverage(scope: LeaderboardScope | undefined): void {
    if (scope === undefined) return;

    const host = document.querySelector<HTMLElement>('.mc-coverage');
    if (!host) return;

    const coverage = scope.coverage;
    const nodes: Node[] = [];

    const line = (text: string): HTMLSpanElement => {
        const el = span(text);
        el.className = 'mc-coverage-line';
        return el;
    };

    const note = (text: string): HTMLParagraphElement => {
        const p = document.createElement('p');
        p.className = 'mc-coverage-note';
        p.textContent = text;
        return p;
    };

    const disclosure = (summaryText: string, body: Node[]): HTMLDetailsElement => {
        const details = document.createElement('details');
        details.className = 'mc-coverage-disclosure';
        const summary = document.createElement('summary');
        summary.textContent = summaryText;
        details.appendChild(summary);
        body.forEach((n) => details.appendChild(n));
        return details;
    };

    // Phase 249 (Pitfall 2, Task 2): the scoped branch now splits in two by selected-server count,
    // mirroring leaderboard.latte's Task 1 three-branch structure node for node —
    //   · exactly ONE name        → today's byte-identical single-server copy
    //   · TWO OR MORE names       → a genuine N-of-M tally against the SAME coverage read
    //   · not scoped              → today's byte-identical fleet copy
    const selectedNames = scope.is_scoped ? scope.server_names : [];

    if (selectedNames.length === 1) {
        // "Ranking <strong>Name</strong> only" — the name is a <strong> child, matching the
        // template, and set through textContent so a server name can never be read as markup.
        const scoped = document.createElement('span');
        scoped.className = 'mc-coverage-line';
        scoped.appendChild(document.createTextNode('Ranking '));
        const strong = document.createElement('strong');
        strong.textContent = selectedNames[0];
        scoped.appendChild(strong);
        scoped.appendChild(document.createTextNode(' only'));
        nodes.push(scoped);

        if (coverage.not_reporting > 0) {
            nodes.push(sep());
            nodes.push(disclosure('not reporting Lane B', [
                note(`${selectedNames[0]} has never sent a daily snapshot. That is not the same as `
                    + '"nobody played" — it is absent from every ranking below.'),
            ]));
        } else {
            nodes.push(sep());
            nodes.push(line('this server is reporting Lane B'));
        }
    } else if (selectedNames.length >= 2) {
        // "Ranking <strong>N</strong> of <strong>M</strong> selected servers" — a genuine N-of-M
        // tally against the same coverage read the fleet branch below uses, never a synthesized
        // single-server shape.
        const scoped = document.createElement('span');
        scoped.className = 'mc-coverage-line';
        scoped.appendChild(document.createTextNode('Ranking '));
        const reporting = document.createElement('strong');
        reporting.textContent = String(coverage.reporting);
        scoped.appendChild(reporting);
        scoped.appendChild(document.createTextNode(' of '));
        const total = document.createElement('strong');
        total.textContent = String(coverage.total);
        scoped.appendChild(total);
        scoped.appendChild(document.createTextNode(' selected servers'));
        nodes.push(scoped);

        if (coverage.not_reporting > 0) {
            nodes.push(sep());
            const list = document.createElement('ul');
            list.className = 'mc-coverage-list';
            for (const name of coverage.not_reporting_names) {
                const li = document.createElement('li');
                li.textContent = name;
                list.appendChild(li);
            }
            nodes.push(disclosure(`${coverage.not_reporting} not reporting Lane B`, [
                list,
                note('These selected servers have never sent a daily snapshot. That is not the '
                    + 'same as "nobody played" — they are absent from every ranking below.'),
            ]));
        } else {
            nodes.push(sep());
            nodes.push(line('every selected server is reporting Lane B'));
        }
    } else {
        const fleet = document.createElement('span');
        fleet.className = 'mc-coverage-line';
        fleet.appendChild(document.createTextNode('Ranking '));
        const reporting = document.createElement('strong');
        reporting.textContent = String(coverage.reporting);
        fleet.appendChild(reporting);
        fleet.appendChild(document.createTextNode(' of '));
        const total = document.createElement('strong');
        total.textContent = String(coverage.total);
        fleet.appendChild(total);
        fleet.appendChild(document.createTextNode(coverage.total === 1 ? ' server' : ' servers'));
        nodes.push(fleet);

        if (coverage.not_reporting > 0) {
            nodes.push(sep());
            const list = document.createElement('ul');
            list.className = 'mc-coverage-list';
            for (const name of coverage.not_reporting_names) {
                const li = document.createElement('li');
                li.textContent = name;
                list.appendChild(li);
            }
            nodes.push(disclosure(`${coverage.not_reporting} not reporting Lane B`, [
                list,
                note('These servers have never sent a daily snapshot. That is not the same as '
                    + '"nobody played" — they are absent from every ranking below.'),
            ]));
        } else {
            nodes.push(sep());
            nodes.push(line('every server is reporting Lane B'));
        }
    }

    host.replaceChildren(...nodes);
}

function renderFetchError(): void {
    // Never leave the previous metric's rows sitting under a new metric label — a stale table is a
    // wrong answer rendered confidently, which is worse than a named failure.
    const region = boardRegion();
    region.replaceChildren(emptyCard(
        "Couldn't load this ranking",
        'The leaderboard could not be refreshed just now. Reload the page to try again.',
    ));
}

function boardMeta(payload: LeaderboardPayload): HTMLElement {
    const meta = document.createElement('div');
    meta.className = 'mc-board-meta';

    meta.appendChild(span(payload.window_label));
    meta.appendChild(sep());
    meta.appendChild(span(`${payload.ranked} ranked`));

    if (payload.unranked > 0) {
        meta.appendChild(sep());
        const unranked = span(`${payload.unranked} unranked`);
        unranked.title = 'These players carry no measurement for this metric. '
            + 'Unranked is not the same as zero.';
        meta.appendChild(unranked);
    }

    // D-05/D-15 summary counter, mirroring leaderboard.latte. DERIVED from the rows the payload
    // already carries — the model gained no new field for this, so the two renderers count the
    // same thing from the same source rather than trusting a number either of them was handed.
    const awaitingBaseline = payload.rows.filter((r) => r.baseline_incomplete === true).length;
    if (awaitingBaseline > 0) {
        meta.appendChild(sep());
        const baseline = span(`${awaitingBaseline} awaiting baseline`);
        baseline.title = 'These players have only one daily snapshot inside this window, so there '
            + 'is no earlier reading to measure a change against. Awaiting a baseline is not the '
            + 'same as measuring zero.';
        meta.appendChild(baseline);
    }

    return meta;
}

function boardTable(payload: LeaderboardPayload, metricLabel: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'table-responsive';

    // Phase 273 D-07/AFK-03 (Plan 12): resolved ONCE for the whole table — every row's value cell
    // reads the SAME gate and the SAME lookup map, matching leaderboard.latte's own $metricKey ===
    // METRIC_ACTIVE_PLAYTIME test (plan 273-11). payload.metric is the SERVER's resolved metric
    // (post-fallback), never the client's raw requested `currentMetric`, so this cannot disagree
    // with what the row values themselves were ranked by.
    const isActiveMetric = payload.metric?.key === 'active_playtime';
    const activePlaytimeTotals = payload.activePlaytimeTotals ?? {};

    const table = document.createElement('table');
    // D-04: a plain Bootstrap table, never the grid library — the metric selector already re-fetches
    // a server-ranked list, and this repo's rowCallback/last-cell clobber trap fires the moment a
    // column is added to a grid instance.
    table.className = 'table table-sm table-striped mc-board-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(th('Rank', 'mc-board-rank-col'));
    headRow.appendChild(th('Player'));
    headRow.appendChild(th(metricLabel, 'text-end'));
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const row of payload.rows) {
        const tr = document.createElement('tr');
        tr.dataset.uuid = row.uuid;

        const rankTd = document.createElement('td');
        rankTd.className = 'mc-board-rank';
        appendRankCell(rankTd, row.rank);
        tr.appendChild(rankTd);

        const playerTd = document.createElement('td');
        // D-15/Phase 249: a flat row carries the SINGLE checked key only when exactly one server is
        // checked, and NOTHING otherwise (zero checked = genuinely fleet-wide, two-or-more checked
        // = a fleet-merged row with no single owning server — scoping it to an arbitrary one of
        // several checked servers would be a false claim). Mirrors leaderboard.latte's identical
        // playerCell rule so the SSR first paint and this AJAX re-render agree.
        playerTd.appendChild(playerCell(row, currentServers.length === 1 ? currentServers[0] : null));
        tr.appendChild(playerTd);

        const valueTd = document.createElement('td');
        valueTd.className = 'mc-board-value';
        if (isActiveMetric) {
            // Matches leaderboard.latte's <td class="mc-board-value" data-metric="active_playtime">
            // (plan 273-11) — the AFK-05/SC-3 disambiguation contract's "distinct, greppable
            // selector" requirement (273-UI-SPEC.md), reproduced here rather than shared, since the
            // two renderers share a data contract, not source.
            valueTd.dataset.metric = 'active_playtime';
        }
        appendValueCell(
            valueTd,
            row.value,
            row.unit,
            row.baseline_incomplete === true,
            undefined,
            isActiveMetric,
            activePlaytimeTotals[row.uuid] ?? null,
        );
        if (row.partial) {
            const star = span('*');
            star.className = 'mc-board-partial';
            star.title = 'Part of this player’s total could not be measured — one of the '
                + 'underlying stat keys is absent from their data, not zero.';
            valueTd.appendChild(document.createTextNode(' '));
            valueTd.appendChild(star);
        }
        tr.appendChild(valueTd);

        tbody.appendChild(tr);
    }
    table.appendChild(tbody);

    wrap.appendChild(table);
    return wrap;
}

function raceSection(group: RaceGroup): HTMLElement {
    const section = document.createElement('section');
    section.className = 'mc-race-group';

    // D-15: THIS GROUP'S OWN server, always — the heading below already names it, so the rows deep-
    // link with it even under "All servers". `?? null` rather than a fall back to any checked
    // server: a group whose server is somehow not on the picker must lose the deep-link scope,
    // never inherit a different server's.
    const groupServerKey = serverKeyById.get(group.mc_server_id) ?? null;

    const heading = document.createElement('h5');
    heading.className = 'mc-race-heading';
    // The heading is the MODEL's own "{server name} Advancement Race" string. Building it here would
    // be a second definition, free to drift from the one the board and the profile both read.
    heading.textContent = group.heading;
    section.appendChild(heading);

    const note = document.createElement('p');
    note.className = 'mc-race-note';
    note.textContent = group.scope_note;
    section.appendChild(note);

    const wrap = document.createElement('div');
    wrap.className = 'table-responsive';

    const table = document.createElement('table');
    table.className = 'table table-sm table-striped mc-board-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(th('Rank', 'mc-board-rank-col'));
    headRow.appendChild(th('Player'));
    headRow.appendChild(th('Progress', 'text-end'));
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const player of group.players) {
        const tr = document.createElement('tr');
        tr.dataset.uuid = player.uuid;

        const rankTd = document.createElement('td');
        rankTd.className = 'mc-board-rank';
        appendRankCell(rankTd, player.rank);
        tr.appendChild(rankTd);

        const playerTd = document.createElement('td');
        playerTd.appendChild(playerCell(player, groupServerKey));
        tr.appendChild(playerTd);

        const progressTd = document.createElement('td');
        progressTd.className = 'mc-board-value';

        const progress = document.createElement('div');
        progress.className = 'progress mc-race-progress';
        progress.setAttribute('role', 'progressbar');
        progress.setAttribute('aria-valuenow', String(player.completed));
        progress.setAttribute('aria-valuemin', '0');
        progress.setAttribute('aria-valuemax', String(player.total));
        const bar = document.createElement('div');
        bar.className = 'progress-bar';
        // Floored to a whole percent, matching the template's own int cast. Set as an inline style
        // from JS, which has no Latte CSS-context escaper between it and the browser.
        bar.style.width = `${Math.floor(player.percent ?? 0)}%`;
        progress.appendChild(bar);
        progressTd.appendChild(progress);

        const count = span(`${formatCount(player.completed)}/${formatCount(player.total)}`);
        count.className = 'mc-race-count';
        progressTd.appendChild(count);

        tr.appendChild(progressTd);
        tbody.appendChild(tr);
    }
    table.appendChild(tbody);

    wrap.appendChild(table);
    section.appendChild(wrap);
    return section;
}

/** D-13: the medal is IN ADDITION to the number, never instead of it. Ties repeat legitimately. */
function appendRankCell(td: HTMLElement, rank: number): void {
    const num = span(String(rank));
    num.className = 'mc-board-rank-num';
    td.appendChild(num);

    if (rank <= 3) {
        const cls = rank === 1 ? 'gold' : rank === 2 ? 'silver' : 'bronze';
        const label = rank === 1 ? 'First place' : rank === 2 ? 'Second place' : 'Third place';
        const medal = document.createElement('span');
        medal.className = `mc-rank-medal mc-rank-medal--${cls}`;
        medal.title = label;
        medal.setAttribute('aria-label', label);
        td.appendChild(document.createTextNode(' '));
        td.appendChild(medal);
    }
}

/**
 * D-15/Phase 249, mirroring leaderboard.latte's `{define playerCell}` rule exactly:
 *   · flat board row, exactly ONE server checked  → that one checked key
 *   · flat board row, zero or TWO-OR-MORE checked → `null`, and the href carries no `?server=` at
 *                                                    all (zero = genuinely fleet-wide; two-or-more
 *                                                    = a fleet-merged row with no single owner)
 *   · advancement-group row                       → that group's OWN key, even under "All servers"
 *
 * `serverKey` is a REQUIRED CALLER DECISION rather than a read of `currentServers` inside this
 * function, because the different board shapes answer it differently — and a JS-rebuilt row that
 * gained a `?server=` the server-rendered row does not have is precisely the renderer-divergence
 * class the Phase 235 regression came from.
 */
function playerCell(
    row: { uuid: string; display_name: string; unresolved: boolean; is_online_mode: boolean },
    serverKey: string | null,
): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'mc-board-player';

    // D-12: the <img> sits OVER a tile carrying the placeholder background, and the error handler
    // sets VISIBILITY (not display) so the placeholder shows through with no layout shift. The
    // display:'none' idiom used elsewhere in this repo hides the image and leaves a hole.
    const tile = document.createElement('span');
    tile.className = 'mc-avatar-tile';
    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(row.uuid)}/32`;
    img.loading = 'lazy';
    img.width = 32;
    img.height = 32;
    img.alt = '';
    img.className = 'mc-player-avatar';
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
    tile.appendChild(img);
    wrap.appendChild(tile);

    const link = document.createElement('a');
    // An empty key would render a bare `?server=`, which reads as "scoped to nothing" rather than
    // "not scoped" — the same guard leaderboard.latte's playerCell applies.
    const scopeSuffix = serverKey !== null && serverKey !== ''
        ? `?server=${encodeURIComponent(serverKey)}`
        : '';
    link.href = `/mc/players/${encodeURIComponent(row.uuid)}${scopeSuffix}`;
    link.title = 'View profile';
    const name = span(row.display_name);
    name.className = `mc-board-name${row.unresolved ? ' mc-board-name--unresolved' : ''}`;
    link.appendChild(name);
    wrap.appendChild(link);

    if (row.unresolved) {
        const badge = span('Resolving…');
        badge.className = 'mc-identity-badge mc-identity-badge--resolving';
        badge.title = 'This player’s current Minecraft name hasn’t been resolved yet. The '
            + 'placeholder is built from their UUID so it can never be mistaken for a real name.';
        wrap.appendChild(badge);
    }

    if (!row.is_online_mode) {
        // D-11: ranked INLINE and badged. Never hidden, never merged into another identity.
        const badge = span('Non-Mojang ID');
        badge.className = 'mc-identity-badge mc-identity-badge--offline';
        badge.title = 'This UUID wasn’t issued by Mojang (likely an offline-mode/cracked '
            + 'account). Ranked normally — never merged with or split from a real identity.';
        wrap.appendChild(badge);
    }

    return wrap;
}

/**
 * A null value means UNRANKED — the metric was never measured for this player. It renders as an
 * em-dash and must never be coerced to 0, which would claim a measurement that never happened.
 *
 * D-05/D-15: `baseline_incomplete` is the same failure wearing a different mask. The model hands
 * back a delta of 0.0 for a player whose only reading inside the window is their FIRST one — there
 * is no earlier row to subtract from, so nothing has been measured yet. Passing that 0.0 to
 * formatValue() prints "0h 0m", indistinguishable from a measured zero. Reuses the unranked
 * em-dash deliberately (one visual language for "not a number we have") with its own title copy.
 *
 * ⚠ THE DEFAULT TITLE STRING BELOW IS BYTE-IDENTICAL TO leaderboard.latte's valueCell, and is
 * deliberately written WITHOUT an apostrophe so it can stay that way — the surrounding strings in
 * this file use typographic quotes while the template uses straight ones, and a disclosure that
 * differs between the two renderers is a disclosure a probe cannot assert once. The bug this closes
 * reached production because a flag existed in the model and in NEITHER renderer; a flag surfaced
 * in only ONE renderer is the same bug, reachable by clicking a window button.
 *
 * Phase 273 D-09 (Plan 12): `absenceTitle` is an OPTIONAL override of the null branch's title,
 * defaulting via `??` — mirroring leaderboard.latte's own `$absenceTitle ?? '...'` widening (plan
 * 273-11) parameter-for-parameter, by convention not inheritance. The byte-identity requirement
 * above now covers the DEFAULT value of this parameter specifically; the override value itself is
 * never invented client-side — it is supplied BY THE SERVER PAYLOAD (a future per-row reason field,
 * were one ever wired for the leaderboard, matching how allStats.ts's per-cell `r` field works,
 * plan 273-12 Task 2) — so the two renderers can never disagree about which sentence a given cell
 * gets. As of this plan, only RANKED players ever appear in a board row (unranked players are
 * excluded from `$board['rows']`/`payload.rows` entirely and counted via the coverage line
 * instead), so `value === null` never actually occurs in a rendered cell today — this widening
 * exists purely for structural parity with the Latte twin, exactly as 273-11-SUMMARY.md records for
 * the template side.
 */
function appendValueCell(
    td: HTMLElement,
    value: number | null,
    unit: string,
    baselineIncomplete = false,
    absenceTitle?: string,
    isActiveMetric = false,
    secondaryTotal: number | null = null,
): void {
    if (value === null) {
        const dash = span('—');
        dash.className = 'mc-board-unmeasured';
        dash.title = absenceTitle ?? 'Not measured for this player — unranked, not zero.';
        td.appendChild(dash);
    } else if (baselineIncomplete) {
        const dash = span('—');
        dash.className = 'mc-board-unmeasured mc-board-baseline';
        dash.title = 'No baseline yet — this window holds only the first daily snapshot for this '
            + 'player, so there is no earlier reading to measure a change against. This is not a '
            + 'measured zero.';
        td.appendChild(dash);
    } else {
        td.appendChild(document.createTextNode(formatValue(value, unit)));
    }

    // Phase 273 D-07/AFK-03 (Plan 12): the stacked secondary line, ONLY when the selected metric is
    // Active Playtime — every other metric (including plain Playtime) renders exactly as above,
    // single line. Reproduces leaderboard.latte's {define valueCell} identical rule (plan 273-11)
    // as a SECOND, INDEPENDENT implementation — this codebase's Latte/JS pairs share a data
    // contract, not source. Gated on isActiveMetric (stable across every row), never on
    // secondaryTotal's presence, so row heights stay stable across the whole board: a row whose
    // total is unavailable (a lookup miss in payload.activePlaytimeTotals) still gets the line, with
    // an em-dash in place of a formatted duration, rather than the row silently growing shorter than
    // its neighbours. Reuses formatValue() — the SAME duration formatter every other branch above
    // uses — never a second formatter.
    if (isActiveMetric) {
        const secondary = document.createElement('span');
        secondary.className = 'mc-metric-secondary';
        secondary.textContent = `Playtime ${secondaryTotal === null ? '—' : formatValue(secondaryTotal, 'duration')}`;
        td.appendChild(secondary);
    }
}

// ---------------------------------------------------------------------------
// The board region — a JS-owned wrapper the re-render replaces wholesale
// ---------------------------------------------------------------------------

/**
 * The template renders the board, the empty card and the per-server race as MUTUALLY EXCLUSIVE
 * branches, so on any given first paint only one of them is in the DOM. There is therefore no
 * server-rendered element that can simply be "shown" or "hidden" to swap between them — the way
 * analytics.ts toggles its always-present chart/empty pair.
 *
 * So this module owns a wrapper of its own, created once and emptied-and-refilled on every render.
 * The server-rendered nodes are removed the first time a re-render happens; until the viewer
 * interacts, the first-paint markup is left exactly as Latte emitted it.
 */
function boardRegion(): HTMLElement {
    let region = document.getElementById('mc-board-region');
    if (region) return region;

    region = document.createElement('div');
    region.id = 'mc-board-region';

    const anchor = document.querySelector('.mc-analytics-selector');
    if (anchor && anchor.parentElement) {
        anchor.insertAdjacentElement('afterend', region);
    } else {
        document.body.appendChild(region);
    }

    // Retire the server-rendered branch now that this module is taking over the region.
    document
        .querySelectorAll('.mc-board-meta, .table-responsive, .mc-board-empty, .mc-race-group')
        .forEach((el) => {
            if (!region!.contains(el)) el.remove();
        });

    return region;
}

function emptyCard(title: string, body: string): HTMLElement {
    const card = document.createElement('div');
    card.className = 'chart chart-empty mc-board-empty';

    const heading = document.createElement('div');
    heading.className = 'chart-empty-title';
    heading.textContent = title;
    card.appendChild(heading);

    const state = document.createElement('div');
    state.className = 'ksat-empty-state';
    const p = document.createElement('p');
    p.className = 'text-muted mb-0';
    p.textContent = body;
    state.appendChild(p);
    card.appendChild(state);

    return card;
}

// ---------------------------------------------------------------------------
// Deep-link origin-row highlight
//
// Purely an affordance for a profile page's "back to the leaderboard" link — NOT a "logged in as"
// feature. This site has no per-viewer Minecraft identity to key a "you" row off (228-UI-SPEC.md
// Open Question 2), and inventing one would be a claim the data cannot support.
// ---------------------------------------------------------------------------

function applyHighlight(): void {
    if (!highlightUuid) return;
    const row = document.querySelector<HTMLElement>(`tr[data-uuid="${cssEscape(highlightUuid)}"]`);
    if (!row) return;
    // The accent-tint idiom from the UI-SPEC's Color contract. Applied as inline style rather than a
    // class because src/css/mc.scss was closed to this plan by 228-07's handoff — and a live var()
    // in a DOM style attribute is the established idiom here (it resolves at paint from the
    // [data-bs-theme] cascade). This is the DOM, not a Highcharts options object, where a live
    // var() would never resolve.
    row.style.backgroundColor = 'rgba(var(--bs-primary-rgb), 0.08)';
    row.style.borderLeft = '2px solid var(--ksat-accent)';
}

/** Minimal attribute-selector escape. UUIDs are hex+hyphen, but the value arrives from the URL. */
function cssEscape(value: string): string {
    return value.replace(/["\\]/g, '\\$&');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readActiveWindow(): string {
    const active = document.querySelector<HTMLElement>('.mc-range-btn.btn-primary[data-window]');
    if (active?.dataset.window) return active.dataset.window;
    const first = document.querySelector<HTMLElement>('.mc-range-btn[data-window]');
    return first?.dataset.window ?? 'all-time';
}

/** Mirrors App\Mc\McMetrics' four unit branches exactly, so a number cannot render one way on the
 *  server-rendered board and another way after an AJAX switch. */
function formatValue(value: number, unit: string): string {
    if (unit === 'duration') {
        const whole = Math.floor(Math.max(value, 0));
        return `${Math.floor(whole / 3600)}h ${Math.floor((whole % 3600) / 60)}m`;
    }
    if (unit === 'ratio') {
        return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (unit === 'distance_cm') {
        const km = Math.round((value / 100000) * 10) / 10;
        return `${km.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
    }
    return formatCount(value);
}

function formatCount(value: number): string {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function span(text: string): HTMLSpanElement {
    const el = document.createElement('span');
    el.textContent = text;
    return el;
}

function sep(): HTMLSpanElement {
    const el = span('·');
    el.className = 'mc-coverage-sep';
    return el;
}

function th(text: string, className?: string): HTMLTableCellElement {
    const el = document.createElement('th');
    el.scope = 'col';
    if (className) el.className = className;
    el.textContent = text;
    return el;
}
